import {
  EarningsInsight,
  EarningsPeriod,
  EarningsPeriods,
  MovePeriod,
  PriceMove,
} from '@api';
import { CheckBox } from '@design/components/checkbox/CheckBox';
import { type AppTableFeatures } from '@design/components/table';
import { Tooltip, TooltipTrigger } from '@design/components/tooltip/Tooltip';
import { ColumnDef, Row, RowSelectionState } from '@tanstack/react-table';
import clsx from 'clsx';
import { t } from 'i18next';
import { DateCellWithEarningsInsight } from '../components/date-cell/DateCellWithEarningsInsight';
import { NumberCell } from '../components/number-cell/NumberCell';
import { PriceCell } from '../components/price-cell/PriceCell';
import styles from '../Earnings.module.scss';
import {
  EARNINGS_COL_WIDTH,
  EARNINGS_COLUMN_ID,
  getFormattedDates,
  PERIOD_COLUMN_ID,
  RELEASED_ON_COLUMN_ID,
  SELECTED_COLUMN_ID,
} from './utils';

const SORT_UNDEFINED = 'last';

const REDUCED_COLUMN_IDS = [
  PERIOD_COLUMN_ID,
  'eps-surprise-dollar',
  'eps-surprise-percentage',
  'revenue-surprise-dollar',
  'revenue-surprise-percentage',
];

export const getReducedColumns = (
  columns: ColumnDef<AppTableFeatures, EarningsPeriod>[]
): ColumnDef<AppTableFeatures, EarningsPeriod>[] =>
  columns.reduce<ColumnDef<AppTableFeatures, EarningsPeriod>[]>(
    (reduced, column) => {
      if ('columns' in column && column.columns) {
        const flattenedColumns = column.columns
          .filter(nestedColumn =>
            REDUCED_COLUMN_IDS.includes(nestedColumn.id ?? '')
          )
          .map(nestedColumn => ({
            ...nestedColumn,
            header: `${String(column.header)} ${String(nestedColumn.header)}`,
          }));

        return [...reduced, ...flattenedColumns];
      }

      return REDUCED_COLUMN_IDS.includes(column.id ?? '')
        ? [...reduced, column]
        : reduced;
    },
    []
  );

export const handlePriceData = (price: PriceMove, period: MovePeriod) =>
  price?.find(p => p.period === period)?.change;

const getPeriodLabel = (row: EarningsPeriod) =>
  `${row.fiscal_period} ${row.fiscal_year}`;

const sortByReportingDate = (
  rowA: Row<AppTableFeatures, EarningsPeriod>,
  rowB: Row<AppTableFeatures, EarningsPeriod>
) => {
  const dateA = new Date(rowA.original.reporting_date);
  const dateB = new Date(rowB.original.reporting_date);

  if (dateA > dateB) {
    return 1;
  } else if (dateA < dateB) {
    return -1;
  } else {
    return 0;
  }
};

const getNumberOfSelectedRowsHeaderText = (
  earningsData: EarningsPeriods,
  rowSelection: RowSelectionState
): string => {
  const totalRows = earningsData.length;
  const numberOfSelectedRows =
    Object.values(rowSelection).filter(Boolean).length;

  if (numberOfSelectedRows === totalRows) {
    return t('widget:earnings.earningsScenario.selectedTotalOf', {
      total: totalRows,
    });
  }

  return t('widget:earnings.earningsScenario.numberOfEarningsSelected', {
    numberOfSelected: numberOfSelectedRows,
    total: totalRows,
  });
};

type GetColumnsProps = {
  getIsAllRowsSelected: () => boolean;
  getIsSomeRowsSelected: () => boolean;
  toggleAllRowsSelection: (selected: boolean) => void;
  updateRowSelection: (key: string, selected: boolean) => void;
  rowSelection: RowSelectionState;
  getIsRowManualChanged: (key: string) => boolean;
  getIsRowMatchingFilters: (key: string) => boolean;
  earningsData: EarningsPeriods;
  earningsInsightByEarningsId?: Map<string, EarningsInsight>;
  isEarningsInsightsFetching: boolean;
  isNewEarningsTabEnabled?: boolean;
};

export const getColumns = ({
  getIsAllRowsSelected,
  getIsSomeRowsSelected,
  toggleAllRowsSelection,
  updateRowSelection,
  rowSelection,
  getIsRowManualChanged,
  getIsRowMatchingFilters,
  earningsData,
  earningsInsightByEarningsId,
  isEarningsInsightsFetching,
  isNewEarningsTabEnabled = false,
}: GetColumnsProps): ColumnDef<AppTableFeatures, EarningsPeriod>[] => {
  const oldHeaderColumns: ColumnDef<AppTableFeatures, EarningsPeriod> = {
    header: getNumberOfSelectedRowsHeaderText(earningsData, rowSelection),
    columns: [
      {
        id: SELECTED_COLUMN_ID,
        header: () => (
          <span className={styles.tableHeaderContent}>
            <CheckBox
              name="all-earnings"
              label=""
              onChange={(_, e) => toggleAllRowsSelection(e.target.checked)}
              checked={getIsAllRowsSelected() || getIsSomeRowsSelected()}
              partial={getIsSomeRowsSelected() && !getIsAllRowsSelected()}
            />
          </span>
        ),
        cell: props => {
          const { row } = props;
          const isRowMatchingFilters = getIsRowMatchingFilters(row.id);
          const isManualUpdated = getIsRowManualChanged(row.id);

          return (
            <Tooltip
              className={styles.checkboxTooltip}
              isTouchDevice={false}
              label={
                <span className={styles.matchingFilterLabel}>
                  {isRowMatchingFilters
                    ? t('widget:earnings.matchesFilter')
                    : t('widget:earnings.outsideFilter')}
                </span>
              }
              trigger={[TooltipTrigger.Hover]}
              inPortal
            >
              <div
                className={styles.wrapperCheckbox}
                onClick={e => e.stopPropagation()}
              >
                <CheckBox
                  className={clsx(styles.styledCheckBox, {
                    [styles.matchingFilters]: isRowMatchingFilters,
                    [styles.manualUpdated]:
                      isManualUpdated && !isRowMatchingFilters,
                  })}
                  name={`earning-${row.id}`}
                  label=""
                  checked={row.getIsSelected()}
                  onChange={() =>
                    updateRowSelection(row.id, !row.getIsSelected())
                  }
                />
              </div>
            </Tooltip>
          );
        },
        size: 50,
      },
      {
        id: EARNINGS_COLUMN_ID,
        header: t('widget:earnings.earnings'),
        cell: props => {
          return (
            <div className={styles.earningsCell}>
              {props.getValue<string>()}
            </div>
          );
        },
        accessorFn: getPeriodLabel,
        sortUndefined: SORT_UNDEFINED,
        sortFn: sortByReportingDate,
        size: EARNINGS_COL_WIDTH,
      },
      {
        id: RELEASED_ON_COLUMN_ID,
        header: t('widget:earnings.releasedOn'),
        cell: props => (
          <DateCellWithEarningsInsight
            date={props.getValue<Date>()}
            earningsInsight={earningsInsightByEarningsId?.get(
              props.row.original.id
            )}
            isFetching={isEarningsInsightsFetching}
          />
        ),
        accessorFn: row => new Date(row.reporting_date),
        sortUndefined: SORT_UNDEFINED,
      },
    ],
  };

  const newHeaderColumns: ColumnDef<AppTableFeatures, EarningsPeriod> = {
    id: PERIOD_COLUMN_ID,
    header: t('widget:earnings.period'),
    cell: props => {
      const { row } = props;
      const { formattedDate } = getFormattedDates(
        new Date(row.original.reporting_date)
      );

      return (
        <div className={styles.periodCell}>
          <span className={styles.periodValue}>{props.getValue<string>()}</span>
          <div className={styles.periodDate}>
            <span>{formattedDate}</span>
          </div>
        </div>
      );
    },
    accessorFn: getPeriodLabel,
    sortUndefined: SORT_UNDEFINED,
    sortFn: sortByReportingDate,
  };

  return [
    ...(isNewEarningsTabEnabled ? [newHeaderColumns] : [oldHeaderColumns]),
    {
      header: t('widget:earnings.eps'),
      columns: [
        {
          id: 'eps-reported',
          header: t('widget:earnings.reported'),
          cell: props => (
            <PriceCell value={props.getValue<number | undefined>()} />
          ),
          accessorFn: row => row.eps.value,
          sortUndefined: SORT_UNDEFINED,
        },
        {
          id: 'eps-surprise-dollar',
          header: t('widget:earnings.surprise', { suffix: '$' }),
          cell: props => (
            <NumberCell value={props.getValue<number | undefined>()} />
          ),
          accessorFn: row => row.eps.surprise,
          sortUndefined: SORT_UNDEFINED,
        },
        {
          id: 'eps-surprise-percentage',
          header: t('widget:earnings.surprise', { suffix: '%' }),
          cell: props => {
            return (
              <span>
                <NumberCell
                  value={props.getValue<number | undefined>()}
                  percentage
                />
              </span>
            );
          },
          accessorFn: row => row.eps.surprise_percentage,
          sortUndefined: SORT_UNDEFINED,
        },
      ],
    },
    {
      header: t('widget:earnings.revenue'),
      columns: [
        {
          id: 'revenue-reported',
          header: t('widget:earnings.reported'),
          cell: props => (
            <PriceCell value={props.getValue<number | undefined>()} />
          ),
          accessorFn: row => row.revenue.value,
          sortUndefined: SORT_UNDEFINED,
        },
        {
          id: 'revenue-surprise-dollar',
          header: t('widget:earnings.surprise', { suffix: '$' }),
          cell: props => (
            <NumberCell value={props.getValue<number | undefined>()} />
          ),
          accessorFn: row => row.revenue.surprise,
          sortUndefined: SORT_UNDEFINED,
        },
        {
          id: 'revenue-surprise-percentage',
          header: t('widget:earnings.surprise', { suffix: '%' }),
          cell: props => (
            <NumberCell
              value={props.getValue<number | undefined>()}
              percentage
            />
          ),
          accessorFn: row => row.revenue.surprise_percentage,
          sortUndefined: SORT_UNDEFINED,
        },
      ],
    },
    {
      header: t('widget:earnings.percentMove'),
      columns: [
        {
          id: 'return-one-day',
          header: t('widget:earnings.1d'),
          cell: props => (
            <NumberCell
              value={props.getValue<number | undefined>()}
              percentage
            />
          ),
          accessorFn: row => handlePriceData(row.price, 'd1'),
          sortUndefined: SORT_UNDEFINED,
        },
        {
          id: 'return-two-days',
          header: t('widget:earnings.2d'),
          cell: props => (
            <NumberCell
              value={props.getValue<number | undefined>()}
              percentage
            />
          ),
          accessorFn: row => handlePriceData(row.price, 'd2'),
          sortUndefined: SORT_UNDEFINED,
        },
        {
          id: 'return-one-week',
          header: t('widget:earnings.1w'),
          cell: props => (
            <NumberCell
              value={props.getValue<number | undefined>()}
              percentage
            />
          ),
          accessorFn: row => handlePriceData(row.price, 'w1'),
          sortUndefined: SORT_UNDEFINED,
        },
        {
          id: 'return-two-weeks',
          header: t('widget:earnings.2w'),
          cell: props => (
            <NumberCell
              value={props.getValue<number | undefined>()}
              percentage
            />
          ),
          accessorFn: row => handlePriceData(row.price, 'w2'),
          sortUndefined: SORT_UNDEFINED,
        },
        {
          id: 'return-one-month',
          header: t('widget:earnings.1m'),
          cell: props => (
            <NumberCell
              value={props.getValue<number | undefined>()}
              percentage
            />
          ),
          accessorFn: row => handlePriceData(row.price, 'm1'),
          sortUndefined: SORT_UNDEFINED,
        },
      ],
    },
  ];
};
